import { useEffect, useState } from "react";
import {
  Loader2, ArrowLeft, ArrowRight, Check, Trash2, ChevronUp, ChevronDown, GripVertical, Plus, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

const PALETTE = [
  "bg-sky-400", "bg-emerald-400", "bg-amber-300", "bg-orange-400", "bg-rose-500",
  "bg-violet-400", "bg-teal-400", "bg-slate-400", "bg-blue-900", "bg-pink-400",
];

const SCALES = [
  { key: "color", label: "Couleurs", hint: "Une pastille de couleur par niveau." },
  { key: "text", label: "Niveaux textuels", hint: "Un libellé par niveau (ex. Satisfaisant)." },
  { key: "number", label: "Niveaux chiffrés", hint: "Une note de 1 à N." },
  { key: "custom", label: "Personnalisé", hint: "Libellé + couleur + symbole." },
];

const DEFAULT_LABELS = ["Très satisfaisant", "Satisfaisant", "À améliorer", "Insuffisant", "Niveau 5", "Niveau 6", "Niveau 7", "Niveau 8", "Niveau 9", "Niveau 10"];

function LevelChip({ level, scale, size = "sm" }) {
  const dot = size === "sm" ? "h-5 w-5" : "h-7 w-7";
  if (level.is_none) {
    return <span className={`inline-flex ${dot} items-center justify-center rounded-md border border-slate-300 bg-white text-slate-400`}>—</span>;
  }
  if (scale === "color") {
    return <span className={`inline-block ${dot} rounded-md ${level.color || "bg-slate-300"}`} title={level.label} />;
  }
  if (scale === "number") {
    return <span className={`inline-flex ${dot} items-center justify-center rounded-md bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200`}>{level.value}</span>;
  }
  if (scale === "custom") {
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-md ${level.color || "bg-slate-100"} px-2 py-1 text-xs font-medium ${level.color ? "text-white" : "text-slate-700"}`}>
        {level.symbol && <span>{level.symbol}</span>}{level.label}
      </span>
    );
  }
  return <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200">{level.label}</span>;
}

export function EditEvalSystemDialog({ open, onOpenChange, classId, currentSystem, onUpdated }) {
  const [step, setStep] = useState("criteria");
  const [saving, setSaving] = useState(false);
  const [crit, setCrit] = useState(["Travail", "Attention"]);
  const [scale, setScale] = useState("color");
  const [levels, setLevels] = useState(
    [0, 1, 2, 3].map((i) => ({ label: DEFAULT_LABELS[i], color: PALETTE[i], symbol: "" }))
  );

  // Pré-remplissage depuis le système actuel à l'ouverture.
  useEffect(() => {
    if (!open || !currentSystem) return;
    setStep("criteria");
    setScale(currentSystem.scale_type || "color");
    const cs = [...(currentSystem.criteria || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setCrit(cs.length ? cs.map((c) => c.label) : ["Travail", "Attention"]);
    const evaluated = (currentSystem.levels || []).filter((l) => !l.is_none);
    // Ordre affiché : meilleur en haut. value la plus haute = meilleur.
    evaluated.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    setLevels(evaluated.length
      ? evaluated.map((l, i) => ({ label: l.label, color: l.color || PALETTE[i % PALETTE.length], symbol: l.symbol || "" }))
      : [0, 1, 2, 3].map((i) => ({ label: DEFAULT_LABELS[i], color: PALETTE[i], symbol: "" })));
  }, [open, currentSystem]);

  const setLevelCount = (n) => {
    setLevels((prev) => {
      const next = [...prev];
      while (next.length < n) { const i = next.length; next.push({ label: DEFAULT_LABELS[i] || `Niveau ${i + 1}`, color: PALETTE[i % PALETTE.length], symbol: "" }); }
      return next.slice(0, n);
    });
  };

  const previewLevels = [
    { id: "none", label: "Non évalué", is_none: true },
    ...levels.map((l, i) => ({ ...l, value: levels.length - i, label: scale === "number" ? String(levels.length - i) : l.label })),
  ];

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        system: {
          scale_type: scale,
          criteria: crit.filter((c) => c.trim()).map((c) => ({ label: c })),
          levels: levels.filter((l) => l.label.trim() || scale === "number").map((l, i) => ({
            label: scale === "number" ? String(levels.length - i) : l.label,
            color: scale === "color" || scale === "custom" ? l.color : null,
            symbol: scale === "custom" ? (l.symbol || null) : null,
          })),
        },
      };
      const { data } = await api.put(`/classes/${classId}/eval-system`, body);
      toast.success(`Système mis à jour (version ${data.version_number}). Les anciens passeports sont conservés.`);
      onOpenChange(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[560px]" data-testid="edit-eval-system-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le système d'évaluation</DialogTitle>
          <DialogDescription>Configuration des critères et des niveaux.</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="edit-version-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          Enregistrer crée une <strong className="mx-1">nouvelle version</strong>. Les passeports déjà remplis conservent l'ancien système ; seules les prochaines évaluations utiliseront le nouveau.
        </div>

        {/* Étape critères */}
        {step === "criteria" && (
          <div className="flex flex-col gap-3 py-1" data-testid="edit-criteria">
            <p className="text-sm text-slate-600 dark:text-slate-300">Quels critères souhaitez-vous évaluer ? (1 à 10)</p>
            <div className="flex flex-col gap-2">
              {crit.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <GripVertical size={16} className="text-slate-300" />
                  <Input value={c} onChange={(e) => setCrit((p) => p.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`edit-criterion-input-${i}`} placeholder={`Critère ${i + 1}`} />
                  <button onClick={() => setCrit((p) => (i > 0 ? [...p.slice(0, i - 1), p[i], p[i - 1], ...p.slice(i + 1)] : p))} className="rounded p-1 text-slate-400 hover:text-slate-700" aria-label="Monter"><ChevronUp size={16} /></button>
                  <button onClick={() => setCrit((p) => (i < p.length - 1 ? [...p.slice(0, i), p[i + 1], p[i], ...p.slice(i + 2)] : p))} className="rounded p-1 text-slate-400 hover:text-slate-700" aria-label="Descendre"><ChevronDown size={16} /></button>
                  <button onClick={() => setCrit((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p))} className="rounded p-1 text-slate-400 hover:text-rose-600" aria-label="Supprimer" data-testid={`edit-criterion-remove-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            {crit.length < 10 && (
              <Button variant="outline" onClick={() => setCrit((p) => [...p, ""])} className="rounded-xl" data-testid="edit-criterion-add"><Plus size={15} className="mr-1.5" /> Ajouter un critère</Button>
            )}
            <div className="mt-2 flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full px-4">Annuler</Button>
              <Button disabled={crit.filter((c) => c.trim()).length === 0} onClick={() => setStep("scale")} className="flex-1 rounded-full bg-blue-900 hover:bg-blue-800 dark:bg-blue-500" data-testid="edit-criteria-next">Continuer <ArrowRight size={16} className="ml-1.5" /></Button>
            </div>
          </div>
        )}

        {/* Étape échelle */}
        {step === "scale" && (
          <div className="flex flex-col gap-4 py-1" data-testid="edit-scale">
            <div>
              <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">Quelle échelle souhaitez-vous utiliser ?</p>
              <div className="grid grid-cols-2 gap-2">
                {SCALES.map((s) => (
                  <button key={s.key} onClick={() => setScale(s.key)} data-testid={`edit-scale-${s.key}`}
                    className={`rounded-xl border p-3 text-left transition-colors ${scale === s.key ? "border-blue-500 bg-blue-50 dark:bg-blue-500/10" : "border-slate-200 hover:border-slate-300 dark:border-white/10"}`}>
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{s.label}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{s.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">Nombre de niveaux (2 à 10)</p>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" className="rounded-full" onClick={() => setLevelCount(Math.max(2, levels.length - 1))} disabled={levels.length <= 2} data-testid="edit-levels-minus">−</Button>
                <span className="w-8 text-center text-lg font-semibold" data-testid="edit-levels-count">{levels.length}</span>
                <Button variant="outline" size="icon" className="rounded-full" onClick={() => setLevelCount(Math.min(10, levels.length + 1))} disabled={levels.length >= 10} data-testid="edit-levels-plus">+</Button>
              </div>
            </div>
            <div className="mt-1 flex gap-2">
              <Button variant="outline" onClick={() => setStep("criteria")} className="rounded-full"><ArrowLeft size={16} /></Button>
              <Button onClick={() => setStep(scale === "number" ? "preview" : "levels")} className="flex-1 rounded-full bg-blue-900 hover:bg-blue-800 dark:bg-blue-500" data-testid="edit-scale-next">Continuer <ArrowRight size={16} className="ml-1.5" /></Button>
            </div>
          </div>
        )}

        {/* Étape niveaux */}
        {step === "levels" && (
          <div className="flex flex-col gap-3 py-1" data-testid="edit-levels">
            <p className="text-sm text-slate-600 dark:text-slate-300">Du meilleur (en haut) au moins bon. « Non évalué » est ajouté automatiquement.</p>
            <div className="flex flex-col gap-2">
              {levels.map((l, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-100 p-2 dark:border-white/10">
                  <span className="w-6 text-center text-xs font-semibold text-slate-400">{levels.length - i}</span>
                  <Input value={l.label} onChange={(e) => setLevels((p) => p.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder={`Niveau ${levels.length - i}`} data-testid={`edit-level-label-${i}`} className="flex-1" />
                  {scale === "custom" && (
                    <Input value={l.symbol} onChange={(e) => setLevels((p) => p.map((x, j) => (j === i ? { ...x, symbol: e.target.value } : x)))} placeholder="⭐" className="w-14 text-center" data-testid={`edit-level-symbol-${i}`} />
                  )}
                  {(scale === "color" || scale === "custom") && (
                    <div className="flex gap-1">
                      {PALETTE.slice(0, 5).map((cl) => (
                        <button key={cl} onClick={() => setLevels((p) => p.map((x, j) => (j === i ? { ...x, color: cl } : x)))}
                          className={`h-6 w-6 rounded-md ${cl} ${l.color === cl ? "ring-2 ring-offset-1 ring-blue-500" : ""}`} aria-label={cl} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-1 flex gap-2">
              <Button variant="outline" onClick={() => setStep("scale")} className="rounded-full"><ArrowLeft size={16} /></Button>
              <Button onClick={() => setStep("preview")} className="flex-1 rounded-full bg-blue-900 hover:bg-blue-800 dark:bg-blue-500" data-testid="edit-levels-next">Prévisualiser <ArrowRight size={16} className="ml-1.5" /></Button>
            </div>
          </div>
        )}

        {/* Étape prévisualisation */}
        {step === "preview" && (
          <div className="flex flex-col gap-4 py-1" data-testid="edit-preview">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Niveaux</p>
              <div className="flex flex-wrap items-center gap-2">
                {previewLevels.map((l) => (
                  <span key={l.id || l.label} className="flex items-center gap-1.5" title={l.label}>
                    <LevelChip level={l} scale={scale} size="sm" />
                    <span className="text-xs text-slate-500">{l.label}</span>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Aperçu côté parent</p>
              <div className="rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                {crit.filter((c) => c.trim()).slice(0, 2).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-sm">
                    <span className="text-slate-600 dark:text-slate-300">{c}</span>
                    <span className="flex items-center gap-1.5"><LevelChip level={previewLevels[i + 1]} scale={scale} size="sm" /><span className="text-slate-500">{previewLevels[i + 1]?.label}</span></span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-1 flex gap-2">
              <Button variant="outline" onClick={() => setStep(scale === "number" ? "scale" : "levels")} className="rounded-full"><ArrowLeft size={16} /></Button>
              <Button onClick={submit} disabled={saving} className="flex-1 rounded-full bg-blue-900 hover:bg-blue-800 dark:bg-blue-500" data-testid="edit-confirm-system">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} className="mr-1.5" /> Enregistrer la nouvelle version</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
